// Imports d'un marché à ciel ouvert (D-308) : plan déjà levé (GeoJSON ou KML) et liste de commerçants
// (CSV). Le fichier est d'abord vérifié : le rapport ligne par ligne s'affiche ; l'import n'est
// proposé que si aucune ligne n'est en erreur, et il est tout ou rien.
import type { RapportImport } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Selection } from '@cc/ui';
import { useState } from 'react';
import { messageErreur } from '../../api/erreurs';
import { useAction } from '../../commun/useAction';
import { useRubriques } from '../recettes/requetes';
import { INVALIDER_MARCHES } from './api';
import { importerCommercants, importerPlan } from './plan-api';
import styles from './marches.module.css';

const LIBELLES_STATUT = { ok: 'Conforme', erreur: 'Erreur', signal: 'À vérifier' } as const;

function Rapport({ rapport }: { rapport: RapportImport }) {
  const lignes = rapport.lignes.filter((l) => l.statut !== 'ok');
  return (
    <div className={styles.pile}>
      <Bandeau type={rapport.valide ? (rapport.applique ? 'succes' : 'info') : 'erreur'}>
        {rapport.applique
          ? 'Import réalisé.'
          : rapport.valide
            ? 'Fichier conforme : vous pouvez confirmer l’import.'
            : 'Fichier refusé : rien n’a été importé. Corrigez les lignes en erreur puis recommencez.'}{' '}
        {Object.entries(rapport.resume)
          .map(([k, v]) => `${k.replace(/_/g, ' ')} : ${v}`)
          .join(', ')}
      </Bandeau>
      {lignes.length ? (
        <table className={styles.tableauPlan}>
          <caption>Lignes en erreur ou à vérifier ({lignes.length})</caption>
          <thead>
            <tr>
              <th scope="col">Ligne</th>
              <th scope="col">Élément</th>
              <th scope="col">Statut</th>
              <th scope="col">Message</th>
            </tr>
          </thead>
          <tbody>
            {lignes.slice(0, 200).map((l, i) => (
              <tr key={`${l.numero}-${i}`}>
                <td>{l.numero || 'fichier'}</td>
                <td className="cc-mono">{l.libelle}</td>
                <td>{LIBELLES_STATUT[l.statut]}</td>
                <td>{l.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

export function ImportPlan({ marcheId }: { marcheId: string }) {
  const [contenu, setContenu] = useState<{ format: 'geojson' | 'kml'; texte: string } | null>(null);
  const [rapport, setRapport] = useState<RapportImport | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [rubrique, setRubrique] = useState('');
  const rubriques = useRubriques();
  const action = useAction({
    executer: (appliquer: boolean) =>
      importerPlan(marcheId, {
        format: contenu!.format,
        contenu: contenu!.texte,
        appliquer,
        rubrique_code: rubrique,
      }),
    invalider: INVALIDER_MARCHES,
    apres: (r) => setRapport(r),
  });
  const lire = async (fichier: File | undefined) => {
    setRapport(null);
    setErreur(null);
    setContenu(null);
    if (!fichier) return;
    if (fichier.size > 5_000_000) return setErreur('Fichier limité à 5 Mo.');
    const texte = await fichier.text();
    setContenu({
      format: /\.kml$/i.test(fichier.name) || texte.trimStart().startsWith('<') ? 'kml' : 'geojson',
      texte,
    });
  };
  return (
    <div className={styles.pile}>
      <Champ
        type="file"
        accept=".geojson,.json,.kml,application/geo+json,application/vnd.google-earth.kml+xml"
        libelle="Importer un plan levé (GeoJSON ou KML)"
        onChange={(e) => void lire(e.target.files?.[0])}
      />
      <Selection
        libelle="Redevance des places importées"
        value={rubrique}
        optionVide="Choisir la rubrique"
        onChange={(e) => setRubrique(e.target.value)}
        options={(rubriques.data ?? [])
          .filter((x) => x.active && x.base_calcul === 'forfait' && x.periodicite === 'mensuelle')
          .map((x) => ({ valeur: x.code, libelle: x.libelle }))}
      />
      <p className={styles.aide}>
        Îlots en polygones (propriétés objet, code, rangees, places_par_rangee) et places en points
        ou polygones (reference, ilot, rangee, rang). Aucune image aérienne n’est utilisée.
      </p>
      {erreur || action.messageErreur ? (
        <Bandeau type="erreur">
          {erreur ?? (action.erreur ? messageErreur(action.erreur) : '')}
        </Bandeau>
      ) : null}
      {rapport ? <Rapport rapport={rapport} /> : null}
      <div className={styles.barreFiltres}>
        <Bouton
          variante="secondaire"
          disabled={!contenu || !rubrique || action.enCours}
          onClick={() => action.lancer(false)}
        >
          Vérifier le plan
        </Bouton>
        <Bouton
          disabled={!contenu || !rubrique || !rapport?.valide || rapport.applique || action.enCours}
          onClick={() => action.lancer(true)}
        >
          Confirmer l’import du plan
        </Bouton>
      </div>
    </div>
  );
}

export function ImportCommercants({ marcheId }: { marcheId: string }) {
  const [texte, setTexte] = useState<string | null>(null);
  const [rapport, setRapport] = useState<RapportImport | null>(null);
  const action = useAction({
    executer: (appliquer: boolean) => importerCommercants(marcheId, { contenu: texte!, appliquer }),
    invalider: INVALIDER_MARCHES,
    apres: (r) => setRapport(r),
  });
  return (
    <div className={styles.pile}>
      <Champ
        type="file"
        accept=".csv,text/csv"
        libelle="Importer une liste de commerçants (CSV)"
        onChange={(e) => {
          setRapport(null);
          void e.target.files?.[0]?.text().then(setTexte);
        }}
      />
      <p className={styles.aide}>
        Colonnes : nom (obligatoire), telephone, activite, place. Les homonymes sont signalés comme
        doublons possibles, jamais fusionnés.
      </p>
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      {rapport ? <Rapport rapport={rapport} /> : null}
      <div className={styles.barreFiltres}>
        <Bouton
          variante="secondaire"
          disabled={!texte || action.enCours}
          onClick={() => action.lancer(false)}
        >
          Vérifier la liste
        </Bouton>
        <Bouton
          disabled={!texte || !rapport?.valide || rapport.applique || action.enCours}
          onClick={() => action.lancer(true)}
        >
          Confirmer l’import des commerçants
        </Bouton>
      </div>
    </div>
  );
}
